import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureModel } from '@/contexts/FeatureModelContext';
import type { ECORequest, ECOMessage } from '@/components/eco-assistant/types';

interface StreamingState {
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
}

export const useStreamingECO = () => {
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    streamingContent: '',
    error: null
  });
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { trackTokenUsage, hasReachedUsageLimit } = useFeatureModel();

  const enhanceContentStreaming = useCallback(async (
    request: ECORequest,
    onMessageUpdate: (message: Partial<ECOMessage>) => void,
    onComplete: (message: ECOMessage) => void
  ) => {
    if (hasReachedUsageLimit('ecoAssistant')) {
      throw new Error('Daily usage limit reached. Please try again tomorrow.');
    }

    // Reset state
    setStreamingState({ isStreaming: true, streamingContent: '', error: null });
    
    try {
      // Use fetch with AbortController for better control
      abortControllerRef.current = new AbortController();
      
      const response = await fetch(
        `https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/eco-assistant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo',
          },
          body: JSON.stringify({ ...request, stream: true }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';
      let templateUsed = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (!data) continue;

            console.log('Frontend received SSE data:', data);

            try {
              const parsed = JSON.parse(data);
              console.log('Frontend parsed SSE:', parsed);
              
              switch (parsed.type) {
                case 'start':
                  console.log('Stream started with template:', parsed.templateUsed);
                  templateUsed = parsed.templateUsed;
                  onMessageUpdate({
                    templateUsed,
                    content: '',
                  });
                  break;
                  
                case 'delta':
                  if (parsed.content) {
                    accumulatedContent += parsed.content;
                    console.log('Frontend delta - new chars:', parsed.content, 'total length:', accumulatedContent.length);
                    
                    // Force immediate state update for streaming content
                    setStreamingState(prev => {
                      const newState = {
                        ...prev,
                        streamingContent: accumulatedContent
                      };
                      console.log('Setting streaming content:', newState.streamingContent.length, 'chars');
                      return newState;
                    });
                    
                    // Force a small delay to ensure visible character-by-character display
                    await new Promise(resolve => setTimeout(resolve, 50));
                  }
                  break;
                  
                case 'complete':
                  console.log('Stream completed, final content length:', (parsed.fullContent || accumulatedContent).length);
                  
                  // Track token usage
                  if (parsed.tokens) {
                    console.log('Tracking token usage:', parsed.tokens);
                    trackTokenUsage('ecoAssistant', parsed.tokens);
                  }
                  
                  const finalMessage: ECOMessage = {
                    id: Date.now().toString(),
                    type: 'assistant',
                    content: parsed.fullContent || accumulatedContent,
                    timestamp: new Date(),
                    templateUsed,
                    tokens: parsed.tokens
                  };
                  
                  onComplete(finalMessage);
                  setStreamingState({ isStreaming: false, streamingContent: '', error: null });
                  return;
                  
                case 'error':
                  console.error('Server sent error:', parsed.error);
                  throw new Error(parsed.error || 'Streaming error occurred');
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError, 'Raw data:', data);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Streaming failed';
      
      setStreamingState(prev => ({ 
        ...prev, 
        isStreaming: false, 
        error: errorMessage 
      }));
      
      toast.error(errorMessage);
      throw error;
    }
  }, [hasReachedUsageLimit, trackTokenUsage]);

  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    setStreamingState({ isStreaming: false, streamingContent: '', error: null });
  }, []);

  return {
    enhanceContentStreaming,
    cancelStreaming,
    isStreaming: streamingState.isStreaming,
    streamingContent: streamingState.streamingContent,
    streamingError: streamingState.error,
  };
};